import { useForm } from '@inertiajs/react'
import type { Topic, User } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  topic: Topic
  teamId: number
  teamMembers: User[]
  meetingId?: number
  onClose: () => void
  // Fired only when the topic was actually resolved, so callers can close a
  // still-open edit panel on success while leaving it untouched on cancel.
  onResolved?: () => void
}

export default function TopicResolveModal({ topic, teamId, teamMembers, meetingId, onClose, onResolved }: Props) {
  const { data, setData, post, processing } = useForm({
    // The backend validates resolution_type against the topic's category;
    // this modal is the plain "Resolved" flow (convert-to-action has its own
    // modal), and 'resolved' is valid for every category.
    resolution_type: 'resolved',
    resolution_notes: '',
    meeting_id: meetingId || null,
    create_action_item: false,
    action_title: topic.title,
    action_owner_id: topic.owner?.id || (teamMembers[0]?.id || ''),
    action_due_date: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/teams/${teamId}/topics/${topic.id}/resolve`, {
      onSuccess: () => {
        onResolved?.()
        onClose()
      },
    })
  }

  return (
    <div className="fixed inset-0 bg-charcoal/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-[#15532E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-lg font-semibold text-text-primary">Resolve Topic</h2>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-[#F5F5F5]/50 p-3 rounded-md">
            <p className="text-sm font-medium text-text-primary">{topic.title}</p>
          </div>

          <div>
            <label className="text-sm font-bold uppercase tracking-widest text-text-primary mb-2 block">
              Resolution Notes
            </label>
            <textarea
              value={data.resolution_notes}
              onChange={e => setData('resolution_notes', e.target.value)}
              rows={4}
              placeholder="How was this resolved? What was decided?"
              className="w-full px-4 py-3 border border-stone rounded-md text-sm resize-y focus:outline-none focus:border-charcoal focus:ring-2 focus:ring-charcoal/15"
            />
          </div>

          <div className="border border-stone rounded-md p-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.create_action_item}
                onChange={e => setData('create_action_item', e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm font-medium text-text-primary">
                Create Action Item as next step
              </span>
            </label>

            {data.create_action_item && (
              <div className="mt-3 space-y-3 pl-6">
                <input
                  type="text"
                  value={data.action_title}
                  onChange={e => setData('action_title', e.target.value)}
                  className="w-full px-3 py-2 border border-stone rounded-md text-sm focus:outline-none focus:border-charcoal focus:ring-2 focus:ring-charcoal/15"
                  placeholder="Action Item title"
                />
                <div className="flex gap-3">
                  <select
                    value={data.action_owner_id}
                    onChange={e => setData('action_owner_id', Number(e.target.value))}
                    className="flex-1 px-3 py-2 border border-stone rounded-md text-sm"
                  >
                    {sortPeople(teamMembers).map(m => (
                      <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={data.action_due_date}
                    onChange={e => setData('action_due_date', e.target.value)}
                    className="px-3 py-2 border border-stone rounded-md text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-text-primary hover:bg-stone/30 rounded-md transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={processing}
              className="bg-[#15532E] text-white px-5 py-2 rounded-md text-sm font-semibold hover:bg-[#0F3D22] disabled:opacity-50 transition-colors">
              {processing ? 'Resolving...' : 'Resolve Topic'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
